// Contexto para gestión global de notificaciones
import React, { createContext, useContext, useState, useCallback } from 'react';
import notificacionesAPI from '../services/notificacionesAPI';
import { useAuth } from './AuthContext';

const NotificacionesContext = createContext();

export const useNotificacionesContext = () => {
  const context = useContext(NotificacionesContext);
  if (!context) {
    throw new Error('useNotificacionesContext debe usarse dentro de NotificacionesProvider');
  }
  return context;
};

export const NotificacionesProvider = ({ children }) => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total: 0, no_leidas: 0 });
  const [loading, setLoading] = useState(false);

  // Cargar estadísticas de notificaciones
  const cargarStats = useCallback(async () => {
    if (!user) {
      setStats({ total: 0, no_leidas: 0 });
      return;
    }
    
    try {
      setLoading(true);
      const response = await notificacionesAPI.obtenerNotificaciones({
        limit: 1, // Solo necesitamos los stats
      });
      
      setStats({
        total: response.total || 0,
        no_leidas: response.no_leidas || 0
      });
    } catch (error) {
      console.error('Error cargando stats de notificaciones:', error);
      setStats({ total: 0, no_leidas: 0 });
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Actualizar contador localmente (optimistic update)
  const actualizarContador = useCallback((cambios) => {
    setStats(prev => ({
      total: Math.max(0, prev.total + (cambios.total || 0)),
      no_leidas: Math.max(0, prev.no_leidas + (cambios.no_leidas || 0))
    }));
  }, []);

  // Marcar una notificación como leída
  const marcarComoLeida = useCallback(async (notificacionId) => {
    try {
      await notificacionesAPI.marcarComoLeida(notificacionId);
      actualizarContador({ no_leidas: -1 });
      return true;
    } catch (error) {
      console.error('Error marcando notificación como leída:', error);
      return false;
    }
  }, [actualizarContador]);

  // Eliminar una notificación
  const eliminarNotificacion = useCallback(async (notificacionId, eraNoLeida = false) => {
    try {
      await notificacionesAPI.eliminarNotificacion(notificacionId);
      actualizarContador({ 
        total: -1, 
        no_leidas: eraNoLeida ? -1 : 0 
      });
      return true;
    } catch (error) {
      console.error('Error eliminando notificación:', error);
      return false;
    }
  }, [actualizarContador]);

  // Marcar todas como leídas
  const marcarTodasLeidas = useCallback(async () => {
    try {
      await notificacionesAPI.marcarTodasLeidas();
      setStats(prev => ({ ...prev, no_leidas: 0 }));
      return true;
    } catch (error) {
      console.error('Error marcando todas como leídas:', error);
      return false;
    }
  }, []);

  const value = {
    stats,
    loading,
    cargarStats,
    actualizarContador,
    marcarComoLeida,
    eliminarNotificacion,
    marcarTodasLeidas
  };

  return (
    <NotificacionesContext.Provider value={value}>
      {children}
    </NotificacionesContext.Provider>
  );
};

export default NotificacionesContext;